import cx from "classnames";
import { addMinutes } from "date-fns";
import { navigate } from "gatsby";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import Image from "../images/tickets-banner.jpg";
import useOnClickOutside from "../utils/use-click-outside";
import Button from "./Button";
import { CommonTranslation } from "./CommonTranslations";
import { LocaleAwareLink } from "./LanguageMenu";
import { useRegion } from "./Region";
import React from "react";
import { AMAZON_SHOP_URL, GIVE_BACK_URL } from "../client-links";

export default function CTAPopup() {
  const [{ ctaPopup }, setCookie] = useCookies(["ctaPopup"]);
  const [mounted, setMounted] = useState(false);

  const ref = useRef(null);
  useOnClickOutside(ref, () => ctaPopup != "dismissed" && dismiss());

  useEffect(() => {
    setMounted(true);
  }, []);

  const dismiss = () => setCookie("ctaPopup", "dismissed", { path: "/", expires: addMinutes(new Date(), 30) });

  if (!mounted) return null;
  if (ctaPopup === "dismissed") return null;

  return (
    <div className="fixed top-0 left-0 z-[70] flex h-full w-full items-center justify-center bg-black bg-opacity-80 px-4 backdrop-blur-sm">
      <div
        ref={ref}
        className="flex w-full max-w-3xl items-center justify-center overflow-hidden rounded-2xl border border-white border-opacity-20 bg-black text-white shadow-2xl"
      >
        <div className="relative flex min-h-[32rem] w-full flex-col overflow-hidden md:min-h-0">
          <img src={Image} className="hidden w-full md:block" />
          <img src={Image} className="block min-h-[32rem] object-cover md:hidden" />
          <div
            className="absolute top-0 left-0 flex h-full w-full flex-col"
            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.08), rgba(0,0,0,0.88) 100%)" }}
          >
            <div className="flex w-full items-center justify-end p-4">
              <button
                onClick={dismiss}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-black bg-opacity-60 text-xl font-light leading-none text-white transition hover:bg-white hover:text-black"
                aria-label="Close popup"
              >
                <span className="-mt-0.5">×</span>
              </button>
            </div>
            <div className="flex-grow"></div>
            <div className="flex w-full flex-col items-center px-5 pb-5 md:px-8 md:pb-8">
              <PopupButtons dismiss={dismiss} />
              <Button
                className="mt-3 h-10 justify-center border-0 text-xs text-white text-opacity-80 transition hover:text-opacity-100"
                onClick={dismiss}
              >
                Continue to homepage
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PopupButtons = ({ dismiss }: { dismiss: () => void }) => {
  const { showTicketsPopup, ticketsButtonText, ticketsUrl } = useRegion();

  if (showTicketsPopup)
    return (
      <div className="w-full md:w-1/2">
        <PopupButtonLink className="mx-auto" to={ticketsUrl ?? ""}>
          {ticketsButtonText ?? "tickets"}
        </PopupButtonLink>
      </div>
    );

  return (
    <div className="flex w-full flex-col items-center justify-center gap-3 md:w-3/4 md:flex-row">
      <button
        onClick={() => {
          dismiss();
          navigate("/watch-now");
        }}
        className={popupButtonClasses}
      >
        <CommonTranslation value="watchNow" />
      </button>
      <PopupButtonLink onClick={dismiss} to={GIVE_BACK_URL}>
        Give Back
      </PopupButtonLink>
      <PopupButtonLink onClick={dismiss} to={AMAZON_SHOP_URL}>
        <CommonTranslation value="shop" />
      </PopupButtonLink>
    </div>
  );
};

const popupButtonClasses =
  "flex w-full flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-primary to-primaryDark px-5 py-3 text-sm font-bold uppercase tracking-[0.08em] shadow-lg transition hover:scale-[1.02] hover:opacity-95 md:w-full";

const PopupButtonLink = ({
  children,
  onClick,
  to,
  className,
}: PropsWithChildren<{ onClick?: () => void; to: string; className?: string }>) => {
  if (/^https?:\/\//.test(to)) {
    return (
      <a href={to} target="_blank" rel="noreferrer" onClick={onClick} className={cx(popupButtonClasses, className)}>
        {children}
      </a>
    );
  }

  return (
    <LocaleAwareLink to={to} onClick={onClick} className={cx(popupButtonClasses, className)}>
      {children}
    </LocaleAwareLink>
  );
};
